import type { OutputPreviewFormValues } from "@/features/adp/schemas/OutputPreview";
import {
  labelForOutputPreviewDataType,
  isOutputPreviewStringType,
} from "@/features/adp/components/destination/dialogs/output-preview/outputPreview.utils";
import { DATA_TYPES } from "@/features/adp/types/data.types";
import { Checkbox } from "@/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
} from "@/ui/form";
import { Input } from "@/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import {
  type CellContext,
  type ColumnDef,
  type HeaderContext,
} from "@tanstack/react-table";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

export type PreviewColumnField = FieldArrayWithId<OutputPreviewFormValues, "columns">;
export type OutputPreviewForm = UseFormReturn<OutputPreviewFormValues>;

export type OutputPreviewTableMeta = {
  fields: PreviewColumnField[];
  form: OutputPreviewForm;
  checkboxState: boolean | "indeterminate";
  setAllSelected: (checked: boolean) => void;
};

const findFieldIndex = (
  fields: PreviewColumnField[],
  rowId: string,
): number => fields.findIndex((f) => f.id === rowId);

type SelectAllHeaderProps = {
  checkboxState: boolean | "indeterminate";
  setAllSelected: (checked: boolean) => void;
};

const SelectAllHeader = ({ checkboxState, setAllSelected }: SelectAllHeaderProps) => (
  <Checkbox
    checked={checkboxState}
    onCheckedChange={(checked) => setAllSelected(checked === true)}
  />
);

type SelectCellProps = {
  rowId: string;
  fields: PreviewColumnField[];
  form: OutputPreviewForm;
};

const SelectCell = ({ rowId, fields, form }: SelectCellProps) => {
  const index = findFieldIndex(fields, rowId);
  if (index < 0) return null;

  return (
    <FormField
      control={form.control}
      name={`columns.${index}.selected`}
      render={({ field }) => (
        <FormItem className="m-0 space-y-0">
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

type DataTypeCellProps = {
  rowId: string;
  fields: PreviewColumnField[];
  form: OutputPreviewForm;
};

function resolveNextLengthForTypeChange(
  nextType: string,
  previousType: string,
  currentLength: number,
): number {
  if (!isOutputPreviewStringType(nextType)) return 0;
  return isOutputPreviewStringType(previousType) ? Math.max(1, currentLength) : 1024;
}

const DataTypeCell = ({ rowId, fields, form }: DataTypeCellProps) => {
  const index = findFieldIndex(fields, rowId);
  if (index < 0) return null;

  return (
    <FormField
      control={form.control}
      name={`columns.${index}.fieldDataType`}
      render={({ field }) => (
        <FormItem className="m-0 space-y-0">
          <Select
            value={field.value}
            onValueChange={(nextType) => {
              const previousType = field.value;
              field.onChange(nextType);
              const currentLength = form.getValues(`columns.${index}.fieldLength`);
              const nextLength = resolveNextLengthForTypeChange(
                nextType,
                previousType,
                currentLength,
              );
              form.setValue(`columns.${index}.fieldLength`, nextLength, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {DATA_TYPES.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {labelForOutputPreviewDataType(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormItem>
      )}
    />
  );
};

type FieldLengthCellProps = {
  rowId: string;
  fields: PreviewColumnField[];
  form: OutputPreviewForm;
};

const FieldLengthCell = ({ rowId, fields, form }: FieldLengthCellProps) => {
  const index = findFieldIndex(fields, rowId);
  if (index < 0) return null;

  const rowDataType = form.watch(`columns.${index}.fieldDataType`);
  const isStringType = isOutputPreviewStringType(rowDataType);

  return (
    <FormField
      control={form.control}
      name={`columns.${index}.fieldLength`}
      render={({ field }) => (
        <FormItem className="m-0 space-y-0">
          <FormControl>
            <Input
              type="number"
              className="h-11"
              disabled={!isStringType}
              // Non-string columns carry length 0 (correct per the Zod
              // superRefine). A static `min={1}` made the native HTML5
              // constraint reject that 0 and silently block the form submit
              // — Save looked unresponsive. Gate `min` on the type.
              min={isStringType ? 1 : 0}
              max={1024}
              value={isStringType ? field.value : 0}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10);
                if (Number.isNaN(parsed)) return;
                field.onChange(Math.min(1024, Math.max(1, parsed)));
              }}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

type PrimaryKeyCellProps = {
  rowId: string;
  fields: PreviewColumnField[];
  form: OutputPreviewForm;
};

const PrimaryKeyCell = ({ rowId, fields, form }: PrimaryKeyCellProps) => {
  const index = findFieldIndex(fields, rowId);
  if (index < 0) return null;

  return (
    <div className="flex w-full items-center justify-center">
      <FormField
        control={form.control}
        name={`columns.${index}.isPrimaryKey`}
        render={({ field }) => (
          <FormItem className="m-0 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => field.onChange(checked === true)}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};

function readOutputPreviewMeta(
  table: { options: { meta?: unknown } },
): OutputPreviewTableMeta {
  return table.options.meta as OutputPreviewTableMeta;
}

function renderSelectHeader(ctx: HeaderContext<PreviewColumnField, unknown>) {
  const { checkboxState, setAllSelected } = readOutputPreviewMeta(ctx.table);
  return (
    <SelectAllHeader
      checkboxState={checkboxState}
      setAllSelected={setAllSelected}
    />
  );
}

function renderSelectCell(ctx: CellContext<PreviewColumnField, unknown>) {
  const { fields, form } = readOutputPreviewMeta(ctx.table);
  return (
    <SelectCell
      rowId={ctx.row.original.id}
      fields={fields}
      form={form}
    />
  );
}

function renderFieldNameCell(ctx: CellContext<PreviewColumnField, unknown>): string {
  return ctx.row.original.fieldName;
}

function renderDataTypeCell(ctx: CellContext<PreviewColumnField, unknown>) {
  const { fields, form } = readOutputPreviewMeta(ctx.table);
  return (
    <DataTypeCell
      rowId={ctx.row.original.id}
      fields={fields}
      form={form}
    />
  );
}

function renderFieldLengthCell(ctx: CellContext<PreviewColumnField, unknown>) {
  const { fields, form } = readOutputPreviewMeta(ctx.table);
  return (
    <FieldLengthCell
      rowId={ctx.row.original.id}
      fields={fields}
      form={form}
    />
  );
}

function renderPrimaryKeyCell(ctx: CellContext<PreviewColumnField, unknown>) {
  const { fields, form } = readOutputPreviewMeta(ctx.table);
  return (
    <PrimaryKeyCell
      rowId={ctx.row.original.id}
      fields={fields}
      form={form}
    />
  );
}

export const OUTPUT_PREVIEW_TABLE_COLUMNS: ColumnDef<PreviewColumnField>[] = [
  {
    id: "select",
    header: renderSelectHeader,
    cell: renderSelectCell,
    size: 20,
  },
  {
    accessorKey: "fieldName",
    header: "Field Name",
    cell: renderFieldNameCell,
    size: 150,
  },
  {
    id: "fieldDataType",
    header: "Data Type",
    cell: renderDataTypeCell,
    size: 120,
  },
  {
    id: "fieldLength",
    header: "Length",
    cell: renderFieldLengthCell,
    size: 90,
  },
  {
    id: "isPrimaryKey",
    header: "Primary Key",
    cell: renderPrimaryKeyCell,
    size: 60,
  },
];
